import {
  ChangeDetectionStrategy,
  Component,
  createEnvironmentInjector,
  EnvironmentInjector,
  inject,
  input,
  runInInjectionContext,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';

import moment from 'moment-timezone';
import { take, combineLatest } from 'rxjs';

import { filterBoolean } from '@core/common-legacy';
import { I18nService } from '@core/i18n-legacy';
import { ReportsLocalFilterTranslations } from '@core/reports-logic';
import { ReportsAppKind } from '@core/reports-types';
import { tellentAppKindToReportsAppKind } from '@core/reports-util';
import { injectTellentAppKind, TellentWebComponent } from '@core/tellent-types';
import { defineCustomElements } from '@tellent/tellent-util';

import { ReportsInitDataService } from './reports-init.data.service';

@Component({
  selector: 'x-reports-shell',
  template: `<ng-content />`,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TellentReportsShellComponent {
  private readonly envInjector = inject(EnvironmentInjector);
  private readonly tellentAppKind = injectTellentAppKind();
  private readonly initData = inject(ReportsInitDataService);
  private readonly i18n = inject(I18nService);

  public companyId = input<string>();
  public localFilterTranslations = input<ReportsLocalFilterTranslations>();

  constructor() {
    combineLatest([
      toObservable(this.companyId).pipe(filterBoolean),
      toObservable(this.localFilterTranslations).pipe(filterBoolean),
    ])
      .pipe(take(1))
      .subscribe(([companyId, localTranslations]) =>
        this.initReports(companyId, localTranslations),
      );
  }

  private async initReports(companyId: string, localTranslations: ReportsLocalFilterTranslations) {
    const module = await import('./reports.provider');
    const { reportsComponents, provideTellentReports, initReportsData } = module;

    if (this.initData.dataInitialized) {
      return;
    }

    this.syncLocale();
    const appKind = this.getReportsAppKind();
    const providers = provideTellentReports({ appKind, companyId, localTranslations });
    const injector = createEnvironmentInjector(providers, this.envInjector);
    defineCustomElements(reportsComponents, injector);
    runInInjectionContext(injector, () => initReportsData());
  }

  private syncLocale() {
    const locale = this.i18n.locale;
    moment.locale(locale);
    this.i18n.localeChanged.subscribe(locale => {
      moment.locale(locale);
    });
  }

  private getReportsAppKind(): ReportsAppKind {
    const appKind = tellentAppKindToReportsAppKind(this.tellentAppKind);
    if (!appKind) {
      throw new Error('Reports initialized for unsupported app kind');
    }
    return appKind;
  }
}

export type TellentReportsShellWebComponent = TellentWebComponent<
  TellentReportsShellComponent,
  'companyId' | 'localFilterTranslations'
>;
